/**
 * What the other streams and the renderer read of the bubbles (see README.md for the coordinates):
 * how a bubble looks this pass, the water's cells, and the C64 tests the players' bubble code needs.
 */

import { HIDDEN, JUST_BLOWN, MEDIUM, SMALL, isBubbleCode, isTrappedCode } from './codes';
import { colOf, subXOf } from './slots';
import { systemOf } from './system';
import { WATER_CELLS, WATER_END_ROW } from './tuning';
import type { Bubble, World } from '../types';

export interface BubbleLook {
  /**
   * Top-left of the 16x16 bubble, VIC sprite frame (playfield pixels: x - 20, y - 45). Empty and
   * special bubbles are drawn where the C64 draws them, from their column and row; a trapped monster
   * is its sprite, at the bubble's X, Y (the two can differ: README.md).
   */
  x: number;
  y: number;
  /** 0 small, 1 medium, 2 full: a shot grows over its steps; everything else is full. */
  size: 0 | 1 | 2;
  /** -1 whole (draw the bubble); 0 the first burst picture, 1 the second (each shown two passes). */
  burst: -1 | 0 | 1;
  /** False while blinked off, flashed off, or gone. */
  visible: boolean;
  /** A trapped monster in its last 16 M-ticks: it flashes (via `visible`), about to escape. */
  flashing: boolean;
  /**
   * The off half of an end-of-life blink (a plain bubble's $48, a trapped one's flash): the C64 hides
   * the bubble (`visible` is false); a renderer may flash it in a warning colour instead.
   */
  blink: boolean;
}

/** How a bubble looks after the last pass. */
export function bubbleLook(b: Bubble): BubbleLook {
  const m = b.motion;
  const trapped = isTrappedCode(m.code);
  const x = trapped ? b.x : colOf(b) * 8 + 0x14 + subXOf(b) * 2;
  const y = trapped ? b.y : m.row * 8 + 0x15 + (m.subY !== 0 ? m.subY - 8 : 0);
  // A shot just blown ($16) is drawn as the small one it becomes (D_7BD4), not full.
  const size = m.code === SMALL || m.code === JUST_BLOWN ? 0 : m.code === MEDIUM ? 1 : 2;
  const burst = m.burst >= 0 && m.burst < 4 ? (m.burst >> 1 === 0 ? 0 : 1) : -1;
  const blink = m.burst < 0 && (m.code === HIDDEN || (trapped && m.flashOff));
  return { x: x & 0xff, y: y & 0xff, size, burst, visible: b.visible, flashing: m.flash, blink };
}

/**
 * The water's drawn cells (cells 1..10 on screen, as D_1B7D draws them) in tile coordinates, tail
 * first; empty when no stream runs.
 */
export function waterCells(world: World): { col: number; row: number }[] {
  const w = systemOf(world).water;
  if (!w) return [];
  const out: { col: number; row: number }[] = [];
  for (let i = 1; i < WATER_CELLS; i++) {
    const row = w.rows[i] ?? WATER_END_ROW;
    if (row >= 4 && row < WATER_END_ROW) out.push({ col: w.cols[i] ?? 0, row: row - 4 });
  }
  return out;
}

/** D_0AAB's test: a bubble a player can push - floating, of type < $24 (not the blink's hidden half). */
export const pushable = (b: Bubble): boolean => b.motion.steps === 0 && isBubbleCode(b.motion.code);

/**
 * L217C's test: a bubble a player can bounce on - type < $24 but not $00 (a small shot) nor $16 (just
 * blown). A medium or full shot counts; the blink's hidden half does not.
 */
export const bounceable = (b: Bubble): boolean =>
  isBubbleCode(b.motion.code) && b.motion.code !== SMALL && b.motion.code !== JUST_BLOWN;

/**
 * The C64's cell of a bubble: column and 2-px sub-column (from X), row and sub-row as the bubble code
 * counts them - the map row a wall test starts from is `row - 4`, not (Y - $15) / 8.
 */
export function bubbleCell(b: Bubble): { col: number; subX: number; row: number; subY: number } {
  return { col: colOf(b), subX: subXOf(b), row: b.motion.row, subY: b.motion.subY };
}
